const STORAGE_KEY = "vp_pharmacy_cart";
const STRIPE_JS_URL = "https://js.stripe.com/v3/";

let stripeJsPromise = null;
let embeddedCheckout = null;
let checkoutOpen = false;

function checkoutBase() {
  const raw = (typeof GLOBAL !== "undefined" && GLOBAL.CHECKOUT_API_URL) || "";
  return String(raw).replace(/\/$/, "");
}

function shopEnabled() {
  return Boolean(checkoutBase());
}

function readCart() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeCart(items) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  window.dispatchEvent(new CustomEvent("vp-cart-updated", { detail: { items } }));
}

function cartCount(items = readCart()) {
  return items.reduce((sum, item) => sum + (item.quantity || 0), 0);
}

function formatMoney(cents, currency = "usd") {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency.toUpperCase(),
    }).format((cents || 0) / 100);
  } catch {
    return `$${((cents || 0) / 100).toFixed(2)}`;
  }
}

async function fetchCatalog() {
  const base = checkoutBase();
  if (!base) return {};
  const res = await fetch(`${base}/api/products`);
  if (!res.ok) throw new Error("Could not load products");
  const data = await res.json();
  const map = {};
  (data.products || []).forEach((p) => {
    map[p.slug] = p;
  });
  return map;
}

function upsertItem(items, next) {
  const idx = items.findIndex((i) => i.slug === next.slug);
  if (idx === -1) {
    items.push({ ...next, quantity: next.quantity || 1 });
  } else {
    items[idx].quantity = Math.min(
      99,
      (items[idx].quantity || 0) + (next.quantity || 1)
    );
    items[idx].title = next.title || items[idx].title;
    items[idx].image = next.image || items[idx].image;
  }
  return items.filter((i) => i.quantity > 0);
}

function setItemQty(items, slug, quantity) {
  return items
    .map((i) => (i.slug === slug ? { ...i, quantity } : i))
    .filter((i) => i.quantity > 0);
}

function updateBadges(items = readCart()) {
  const count = cartCount(items);
  document.querySelectorAll(".js-cart-count").forEach((el) => {
    el.textContent = String(count);
    el.hidden = count === 0;
  });
}

function renderDrawer(catalog = {}) {
  const list = document.querySelector(".js-cart-items");
  const empty = document.querySelector(".js-cart-empty");
  const footer = document.querySelector(".js-cart-footer");
  const subtotalEl = document.querySelector(".js-cart-subtotal");
  if (!list) return;

  const items = readCart();
  list.innerHTML = "";

  if (!items.length) {
    if (empty) empty.hidden = false;
    if (footer) footer.hidden = true;
    return;
  }

  if (empty) empty.hidden = true;
  if (footer) footer.hidden = false;

  let subtotal = 0;
  let currency = "usd";

  items.forEach((item) => {
    const product = catalog[item.slug];
    const price = product?.price ?? null;
    const cur = product?.currency || "usd";
    currency = cur;
    if (price != null) subtotal += price * item.quantity;

    const row = document.createElement("div");
    row.className = "vp-cart-item";
    row.innerHTML = `
      <div class="vp-cart-item-media">
        ${
          item.image
            ? `<img src="${item.image}" alt="" loading="lazy" width="64" height="64">`
            : ""
        }
      </div>
      <div class="vp-cart-item-body">
        <div class="vp-cart-item-title">${item.title || item.slug}</div>
        <div class="vp-cart-item-meta">
          ${
            price != null
              ? `<span>${formatMoney(price, cur)}</span>`
              : `<span class="vp-cart-item-missing">Price unavailable</span>`
          }
        </div>
        <div class="vp-cart-item-qty">
          <button type="button" class="js-cart-qty" data-slug="${item.slug}" data-delta="-1" aria-label="Decrease quantity">−</button>
          <span>${item.quantity}</span>
          <button type="button" class="js-cart-qty" data-slug="${item.slug}" data-delta="1" aria-label="Increase quantity">+</button>
          <button type="button" class="js-cart-remove" data-slug="${item.slug}">Remove</button>
        </div>
      </div>
    `;
    list.appendChild(row);
  });

  if (subtotalEl) {
    subtotalEl.textContent = formatMoney(subtotal, currency);
  }
}

function setDrawerOpen(open) {
  const drawer = document.querySelector(".js-cart-drawer");
  const underlay = document.querySelector(".js-cart-underlay");
  if (!drawer) return;
  drawer.dataset.open = open ? "true" : "false";
  if (underlay) underlay.dataset.open = open ? "true" : "false";
  document.body.classList.toggle("vp-cart-open", open);
}

function setCheckoutStatus(message) {
  const overlayStatus = document.querySelector(".js-checkout-status");
  const cartStatus = document.querySelector(".js-cart-status");
  if (overlayStatus) overlayStatus.textContent = message || "";
  if (cartStatus) cartStatus.textContent = message || "";
}

function destroyEmbeddedCheckout() {
  if (embeddedCheckout) {
    try {
      embeddedCheckout.destroy();
    } catch {
      /* already torn down */
    }
    embeddedCheckout = null;
  }
  const mount = document.querySelector(".js-embedded-checkout");
  if (mount) mount.innerHTML = "";
}

function setCheckoutOpen(open) {
  const overlay = document.querySelector(".js-checkout-overlay");
  if (!overlay) return;
  checkoutOpen = open;
  overlay.hidden = !open;
  overlay.dataset.open = open ? "true" : "false";
  document.body.classList.toggle("vp-checkout-open", open);
  if (!open) {
    destroyEmbeddedCheckout();
    setCheckoutStatus("");
  }
}

function loadStripeJs() {
  if (window.Stripe) return Promise.resolve(window.Stripe);
  if (stripeJsPromise) return stripeJsPromise;

  stripeJsPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${STRIPE_JS_URL}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(window.Stripe));
      existing.addEventListener("error", () =>
        reject(new Error("Could not load Stripe.js"))
      );
      return;
    }
    const script = document.createElement("script");
    script.src = STRIPE_JS_URL;
    script.async = true;
    script.onload = () => resolve(window.Stripe);
    script.onerror = () => reject(new Error("Could not load Stripe.js"));
    document.head.appendChild(script);
  });

  return stripeJsPromise;
}

async function createCheckoutSession() {
  const base = checkoutBase();
  const items = readCart();
  const res = await fetch(`${base}/api/checkout`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      ui_mode: "embedded",
      items: items.map((i) => ({ slug: i.slug, quantity: i.quantity })),
    }),
  });
  const data = await res.json();
  if (!res.ok) {
    throw new Error(data.error || "Checkout failed");
  }
  if (!data.clientSecret || !data.publishableKey) {
    throw new Error("Checkout did not return an embedded session");
  }
  return data;
}

async function startCheckout() {
  const base = checkoutBase();
  const items = readCart();
  const checkoutBtn = document.querySelector(".js-cart-checkout");
  if (!base || !items.length || checkoutOpen) return;

  if (checkoutBtn) checkoutBtn.disabled = true;
  setCheckoutStatus("Opening checkout…");

  try {
    setDrawerOpen(false);
    setCheckoutOpen(true);

    const StripeCtor = await loadStripeJs();
    if (!StripeCtor) throw new Error("Stripe.js unavailable");

    let sessionPromise = createCheckoutSession();

    const fetchClientSecret = async () => {
      const data = await sessionPromise;
      return data.clientSecret;
    };

    const session = await sessionPromise;
    const stripe = StripeCtor(session.publishableKey);
    if (typeof stripe.initEmbeddedCheckout !== "function") {
      throw new Error("This Stripe.js build does not support embedded checkout");
    }

    embeddedCheckout = await stripe.initEmbeddedCheckout({ fetchClientSecret });
    const mount = document.querySelector(".js-embedded-checkout");
    if (!mount) throw new Error("Checkout mount missing");
    embeddedCheckout.mount(mount);
    setCheckoutStatus("");
  } catch (err) {
    setCheckoutOpen(false);
    setDrawerOpen(true);
    setCheckoutStatus(err.message || "Checkout failed");
  } finally {
    if (checkoutBtn) checkoutBtn.disabled = false;
  }
}

async function refreshUI(catalogPromise) {
  const items = readCart();
  updateBadges(items);
  let catalog = {};
  try {
    catalog = await catalogPromise;
  } catch {
    catalog = {};
  }
  renderDrawer(catalog);
  return catalog;
}

function hydratePrices(catalog) {
  document.querySelectorAll("[data-product-slug]").forEach((el) => {
    const slug = el.getAttribute("data-product-slug");
    const product = catalog[slug];
    const priceEl = el.querySelector(".js-product-price");
    const btn = el.querySelector(".js-add-to-cart");
    if (!product) {
      if (priceEl) priceEl.textContent = "";
      if (btn) btn.disabled = true;
      return;
    }
    if (priceEl) priceEl.textContent = formatMoney(product.price, product.currency);
    if (btn) {
      btn.disabled = false;
      btn.dataset.slug = product.slug;
    }
  });
}

export default function initCart() {
  if (!shopEnabled()) return;

  const catalogPromise = fetchCatalog().catch(() => ({}));

  document.querySelectorAll(".js-cart-open").forEach((btn) => {
    btn.addEventListener("click", (e) => {
      e.preventDefault();
      if (checkoutOpen) return;
      setDrawerOpen(true);
      refreshUI(catalogPromise);
    });
  });

  document.querySelectorAll(".js-cart-close").forEach((btn) => {
    btn.addEventListener("click", (e) => {
      e.preventDefault();
      setDrawerOpen(false);
    });
  });

  const underlay = document.querySelector(".js-cart-underlay");
  if (underlay) {
    underlay.addEventListener("click", () => setDrawerOpen(false));
  }

  document.querySelectorAll(".js-checkout-close").forEach((btn) => {
    btn.addEventListener("click", (e) => {
      e.preventDefault();
      setCheckoutOpen(false);
    });
  });

  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && checkoutOpen) {
      setCheckoutOpen(false);
    }
  });

  document.addEventListener("click", (e) => {
    const addBtn = e.target.closest(".js-add-to-cart");
    if (addBtn) {
      e.preventDefault();
      e.stopPropagation();
      const slug = addBtn.dataset.slug;
      if (!slug || addBtn.disabled) return;
      const root = addBtn.closest("[data-product-slug]") || addBtn;
      const title =
        addBtn.dataset.title ||
        root.getAttribute("data-product-title") ||
        slug;
      const image =
        addBtn.dataset.image ||
        root.getAttribute("data-product-image") ||
        "";
      writeCart(
        upsertItem(readCart(), {
          slug,
          title,
          image,
          quantity: 1,
        })
      );
      setDrawerOpen(true);
      refreshUI(catalogPromise);
      return;
    }

    const qtyBtn = e.target.closest(".js-cart-qty");
    if (qtyBtn) {
      const slug = qtyBtn.dataset.slug;
      const delta = Number(qtyBtn.dataset.delta || 0);
      const items = readCart();
      const current = items.find((i) => i.slug === slug);
      if (!current) return;
      writeCart(setItemQty(items, slug, Math.max(0, current.quantity + delta)));
      refreshUI(catalogPromise);
      return;
    }

    const removeBtn = e.target.closest(".js-cart-remove");
    if (removeBtn) {
      writeCart(setItemQty(readCart(), removeBtn.dataset.slug, 0));
      refreshUI(catalogPromise);
    }
  });

  const checkoutBtn = document.querySelector(".js-cart-checkout");
  if (checkoutBtn) {
    checkoutBtn.addEventListener("click", (e) => {
      e.preventDefault();
      startCheckout();
    });
  }

  window.addEventListener("vp-cart-updated", () => updateBadges());

  catalogPromise.then((catalog) => {
    hydratePrices(catalog);
    refreshUI(Promise.resolve(catalog));
  });

  updateBadges();
}
